// Deterministic scoring engine.
//
// Architecture: Raw data -> Normalization -> Rules/scoring engine -> Signal.
// These are pure functions with no side effects, so the exact same logic can
// be lifted into a server-side job later without modification.

import {
  INDICATORS,
  INDICATORS_BY_ID,
  GROUP_LABELS,
  type IndicatorDef,
  type IndicatorGroup,
  type ScoreDomain,
} from './indicators';
import type {
  Observation,
  IndicatorSnapshot,
  DomainScore,
  SignalState,
  CyclePhase,
  ScoreSnapshot,
} from './types';

/** Clamp a number to a range. */
export function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Normalize a raw value into a 0-100 supportive sub-score using the
 * indicator's reference band and direction.
 */
export function normalize(def: IndicatorDef, value: number): number {
  const { low, high } = def.band;
  const t = (value - low) / (high - low); // 0..1 across the band
  const clamped = clamp(t, 0, 1);
  const supportive = def.direction === 1 ? clamped : 1 - clamped;
  return Math.round(supportive * 100);
}

const STATE_LABELS: Record<ScoreDomain, Record<string, string>> = {
  liquidity: {
    high: 'FAVORABLE',
    mid: 'NEUTRAL',
    low: 'TIGHTENING',
  },
  bch: {
    high: 'STRONG',
    mid: 'DEVELOPING',
    low: 'WEAK',
  },
};

export function domainLabel(domain: ScoreDomain, score: number): string {
  if (domain === 'bch') {
    if (score >= 75) return 'STRONG';
    if (score >= 60) return 'IMPROVING';
    if (score >= 45) return 'DEVELOPING';
    if (score >= 30) return 'STABILIZING';
    return 'WEAK';
  }
  if (score >= 65) return 'FAVORABLE';
  if (score >= 50) return 'SUPPORTIVE';
  if (score >= 40) return 'NEUTRAL';
  if (score >= 25) return 'TIGHTENING';
  return 'RESTRICTIVE';
}

/**
 * Derive an honest signal state from the score, its recent trend, and
 * confidence. Avoids simplistic single-word bullish signals.
 */
export function signalState(
  score: number,
  trend30d: number,
  confidence: number,
): SignalState {
  if (confidence < 0.5) return 'neutral';
  if (score >= 65 && trend30d >= 6) return 'confirmed_supportive';
  if (score >= 55 && trend30d > 0) return 'developing';
  if (score <= 30 && trend30d <= -6) return 'confirmed_negative';
  if (score <= 40 && trend30d < 0) return 'warning';
  return 'neutral';
}

export const SIGNAL_META: Record<
  SignalState,
  { label: string; emoji: string; className: string }
> = {
  confirmed_supportive: {
    label: 'CONFIRMED SUPPORTIVE',
    emoji: '🟢',
    className: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10',
  },
  developing: {
    label: 'DEVELOPING',
    emoji: '🟢',
    className: 'text-emerald-300 border-emerald-500/30 bg-emerald-500/5',
  },
  neutral: {
    label: 'NEUTRAL',
    emoji: '🟡',
    className: 'text-yellow-300 border-yellow-500/30 bg-yellow-500/10',
  },
  warning: {
    label: 'WARNING',
    emoji: '🟠',
    className: 'text-orange-400 border-orange-500/40 bg-orange-500/10',
  },
  confirmed_negative: {
    label: 'CONFIRMED NEGATIVE',
    emoji: '🔴',
    className: 'text-red-400 border-red-500/40 bg-red-500/10',
  },
};

function computeDomain(
  domain: ScoreDomain,
  observations: Map<string, Observation>,
  trend30d: number,
): DomainScore {
  const defs = INDICATORS.filter((d) => d.domain === domain);
  const snapshots: IndicatorSnapshot[] = [];

  let weightAvailable = 0;
  let weightTotal = 0;
  let weightedSum = 0;

  for (const def of defs) {
    weightTotal += def.weight;
    const obs = observations.get(def.id);
    if (!obs || obs.value === null || obs.quality === 'unavailable') {
      snapshots.push({
        indicatorId: def.id,
        value: obs?.value ?? null,
        timestamp: obs?.timestamp ?? 0,
        source: def.source,
        frequency: def.frequency,
        quality: 'unavailable',
        subScore: 50,
        contribution: 0,
      });
      continue;
    }
    const subScore = normalize(def, obs.value);
    weightAvailable += def.weight;
    weightedSum += subScore * def.weight;
    snapshots.push({
      ...obs,
      subScore,
      contribution: 0, // filled after we know the final score
    });
  }

  const score = weightAvailable > 0
    ? Math.round(weightedSum / weightAvailable)
    : 50;

  // Confidence reflects how much of the domain weight has fresh data.
  const confidence = weightTotal > 0 ? weightAvailable / weightTotal : 0;

  // Contribution: each indicator's signed deviation from neutral (50),
  // scaled by weight and expressed in points of the final score.
  for (const snap of snapshots) {
    const def = INDICATORS_BY_ID[snap.indicatorId];
    if (snap.quality === 'unavailable' || weightAvailable === 0) continue;
    const deviation = (snap.subScore - 50) * (def.weight / weightAvailable);
    snap.contribution = Math.round(deviation);
  }

  // Group contributions for the transparent breakdown.
  const groupMap = new Map<IndicatorGroup, number>();
  for (const snap of snapshots) {
    const def = INDICATORS_BY_ID[snap.indicatorId];
    groupMap.set(def.group, (groupMap.get(def.group) ?? 0) + snap.contribution);
  }
  const groupContributions = Array.from(groupMap.entries())
    .map(([group, points]) => ({
      group,
      label: GROUP_LABELS[group],
      points: Math.round(points),
    }))
    .sort((a, b) => Math.abs(b.points) - Math.abs(a.points));

  const label = domainLabel(domain, score);
  const state = signalState(score, trend30d, confidence);

  return {
    domain,
    score,
    label,
    state,
    confidence,
    indicators: snapshots,
    groupContributions,
  };
}

/** Determine the liquidity cycle phase from the score and its multi-week trend. */
/**
 * Level-only phase classification, used when we don't yet have enough real
 * history to trust the 30-day trend. Purely describes where the current score
 * sits, with no directional claim.
 */
function phaseFromLevel(liquidityScore: number): CyclePhase {
  if (liquidityScore >= 80) return 'mania';
  if (liquidityScore >= 62) return 'expansion';
  if (liquidityScore >= 45) return 'easing';
  if (liquidityScore >= 30) return 'bottoming';
  return 'tightening';
}

/**
 * Determine the liquidity cycle phase from the score and its multi-week trend.
 *
 * `trendReliable` guards against calling a trend-driven phase (e.g. "easing")
 * off a trend built on too little history — a data-migration blip must never
 * masquerade as a genuine 30-day improvement. When the trend isn't yet
 * trustworthy we fall back to a level-only classification.
 *
 * Trend-driven upgrades additionally require a minimum absolute level, so
 * "easing" means the score is genuinely climbing OUT of a low rather than
 * ticking up while still deeply restrictive.
 */
export function computePhase(
  liquidityScore: number,
  trend30d: number,
  trendReliable = true,
): CyclePhase {
  if (!trendReliable) return phaseFromLevel(liquidityScore);

  if (liquidityScore >= 80 && trend30d > 0) return 'mania';
  if (liquidityScore >= 62 && trend30d >= 4) return 'expansion';
  // "Easing" requires BOTH a real upward trend AND a level that has climbed off
  // the floor. A rising score that is still restrictive (< 45) is "bottoming",
  // not easing.
  if (trend30d >= 3 && liquidityScore >= 45) return 'easing';
  if (trend30d >= 3 && liquidityScore < 45) return 'bottoming';
  if (liquidityScore <= 30 && trend30d > -2) return 'bottoming';
  return 'tightening';
}

export const PHASE_META: Record<
  CyclePhase,
  { label: string; order: number; description: string }
> = {
  tightening: { label: 'Tightening', order: 0, description: 'Financial conditions are restrictive and liquidity is contracting.' },
  bottoming: { label: 'Bottoming', order: 1, description: 'Tightening is decelerating; conditions may be forming a base.' },
  easing: { label: 'Easing', order: 2, description: 'Liquidity conditions are improving but broad expansion is not yet confirmed.' },
  expansion: { label: 'Expansion', order: 3, description: 'Liquidity is broadly expanding and supportive of risk assets.' },
  mania: { label: 'Mania', order: 4, description: 'Liquidity is abundant and risk appetite is elevated.' },
};

export const CYCLE_ORDER: CyclePhase[] = [
  'tightening',
  'bottoming',
  'easing',
  'expansion',
  'mania',
];

/**
 * Build a full score snapshot from a list of observations. `trends` supplies
 * the 30-day score change per domain (0 when unknown) so state/phase can be
 * computed honestly.
 */
export function buildSnapshot(
  observations: Observation[],
  trends: { liquidity: number; bch: number },
  timestamp = Date.now(),
  trendReliable = true,
): ScoreSnapshot {
  const map = new Map(observations.map((o) => [o.indicatorId, o]));
  const liquidity = computeDomain('liquidity', map, trends.liquidity);
  const bch = computeDomain('bch', map, trends.bch);
  const phase = computePhase(liquidity.score, trends.liquidity, trendReliable);
  return { timestamp, liquidity, bch, phase, observations };
}

// Re-export for convenience.
export { STATE_LABELS };
